/**
 * App-wide idle session timeout helpers (pure — no React).
 *
 * Default: 40 minutes idle → 60s warning → signOut.
 * Activity continues to count while the tab is hidden (stricter security).
 *
 * Debug (staging click-test): localStorage.setItem('shiftyIdleDebug','1')
 * shortens to 45s idle / 15s warning. Remove after testing.
 */

export const IDLE_TIMEOUT_MS = 40 * 60 * 1000;
export const IDLE_WARNING_COUNTDOWN_MS = 60 * 1000;
export const IDLE_DEBUG_TIMEOUT_MS = 45 * 1000;
export const IDLE_DEBUG_WARNING_MS = 15 * 1000;
export const IDLE_MOUSE_THROTTLE_MS = 1000;
export const IDLE_BUSY_HEARTBEAT_MS = 30 * 1000;

/** Cross-tab last-activity timestamp (ms since epoch). */
export const IDLE_ACTIVITY_STORAGE_KEY = 'shifty-idle-last-activity';
export const IDLE_DEBUG_STORAGE_KEY = 'shiftyIdleDebug';
export const IDLE_BROADCAST_CHANNEL = 'shifty-idle-session';

/**
 * @returns {{ idleMs: number, warnMs: number, debug: boolean }}
 */
export function resolveIdleTimeouts() {
  let debug = false;
  try {
    debug =
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(IDLE_DEBUG_STORAGE_KEY) === '1';
  } catch {
    debug = false;
  }
  if (debug) {
    return {
      idleMs: IDLE_DEBUG_TIMEOUT_MS,
      warnMs: IDLE_DEBUG_WARNING_MS,
      debug: true,
    };
  }
  return {
    idleMs: IDLE_TIMEOUT_MS,
    warnMs: IDLE_WARNING_COUNTDOWN_MS,
    debug: false,
  };
}

/**
 * Routes where idle logout must not run (unauthenticated / public).
 * @param {string} pathname
 * @returns {boolean}
 */
export function isIdleExemptPath(pathname) {
  const p = typeof pathname === 'string' ? pathname : '';
  if (!p || p === '/') return true;
  if (p === '/login' || p === '/reset') return true;
  if (p.startsWith('/embed/')) return true;
  return false;
}

/**
 * Keys that count as reading activity (mitigate false logout on long docs).
 * @param {string} key
 * @returns {boolean}
 */
export function isIdleActivityKey(key) {
  if (!key) return false;
  const k = String(key);
  return (
    k === 'ArrowUp' ||
    k === 'ArrowDown' ||
    k === 'ArrowLeft' ||
    k === 'ArrowRight' ||
    k === 'PageUp' ||
    k === 'PageDown' ||
    k === 'Home' ||
    k === 'End' ||
    k === ' ' ||
    k === 'Spacebar'
  );
}

/**
 * Throttle helper: returns true if enough time has passed since last.
 * @param {number} now
 * @param {number} last
 * @param {number} minGapMs
 * @returns {boolean}
 */
export function shouldAcceptThrottledActivity(now, last, minGapMs) {
  if (!Number.isFinite(last) || last <= 0) return true;
  return now - last >= minGapMs;
}

/**
 * @param {number} now
 * @param {number} lastActivityAt
 * @param {number} idleMs
 * @returns {boolean}
 */
export function shouldShowIdleWarning(now, lastActivityAt, idleMs) {
  if (!Number.isFinite(lastActivityAt) || lastActivityAt <= 0) return false;
  return now - lastActivityAt >= idleMs;
}

/**
 * @param {number} warningStartedAt
 * @param {number} now
 * @param {number} warnMs
 * @returns {number} seconds remaining (ceil), min 0
 */
export function idleWarningSecondsRemaining(warningStartedAt, now, warnMs) {
  if (!Number.isFinite(warningStartedAt) || warningStartedAt <= 0) return 0;
  const elapsed = now - warningStartedAt;
  const left = Math.ceil((warnMs - elapsed) / 1000);
  return Math.max(0, left);
}

/**
 * @param {number} warningStartedAt
 * @param {number} now
 * @param {number} warnMs
 * @returns {boolean}
 */
export function shouldForceIdleSignOut(warningStartedAt, now, warnMs) {
  if (!Number.isFinite(warningStartedAt) || warningStartedAt <= 0) return false;
  return now - warningStartedAt >= warnMs;
}

/** @type {Set<(meta?: { reason?: string }) => void>} */
const activityListeners = new Set();

/**
 * Global activity bump for long-running jobs (AI, uploads).
 * IdleSessionProvider subscribes; callers need not import React.
 *
 * @param {{ reason?: string }} [meta]
 */
export function reportAppActivity(meta = {}) {
  activityListeners.forEach((fn) => {
    try {
      fn(meta);
    } catch {
      /* ignore listener errors */
    }
  });
}

/**
 * @param {(meta?: { reason?: string }) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeAppActivity(fn) {
  if (typeof fn !== 'function') return () => {};
  activityListeners.add(fn);
  return () => {
    activityListeners.delete(fn);
  };
}

/** @type {null | (() => { dirty: boolean, label?: string, flush?: () => Promise<void>|void })} */
let unsavedWorkChecker = null;

/**
 * Register a dirty-work probe (e.g. Image Editor). One active checker.
 * Persistence phase can return `{ dirty, flush }` to autosave on warn.
 *
 * @param {() => { dirty: boolean, label?: string, flush?: () => Promise<void>|void }} fn
 * @returns {() => void}
 */
export function registerIdleUnsavedWorkChecker(fn) {
  unsavedWorkChecker = typeof fn === 'function' ? fn : null;
  return () => {
    if (unsavedWorkChecker === fn) unsavedWorkChecker = null;
  };
}

/**
 * @returns {{ dirty: boolean, label?: string, flush?: () => Promise<void>|void }}
 */
export function getIdleUnsavedWorkStatus() {
  if (!unsavedWorkChecker) return { dirty: false };
  try {
    const status = unsavedWorkChecker();
    if (!status || typeof status !== 'object') return { dirty: false };
    return {
      dirty: Boolean(status.dirty),
      label: typeof status.label === 'string' ? status.label : undefined,
      flush: typeof status.flush === 'function' ? status.flush : undefined,
    };
  } catch {
    return { dirty: false };
  }
}

/**
 * Write last-activity timestamp for other tabs.
 * @param {number} timestamp
 */
export function publishIdleActivityTimestamp(timestamp) {
  const ts = Number(timestamp) || Date.now();
  try {
    localStorage.setItem(IDLE_ACTIVITY_STORAGE_KEY, String(ts));
  } catch {
    /* ignore quota / private mode */
  }
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(IDLE_BROADCAST_CHANNEL);
      ch.postMessage({ type: 'activity', at: ts });
      ch.close();
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {StorageEvent} event
 * @returns {number|null}
 */
export function parseIdleActivityStorageEvent(event) {
  if (!event || event.key !== IDLE_ACTIVITY_STORAGE_KEY) return null;
  const n = Number(event.newValue);
  return Number.isFinite(n) && n > 0 ? n : null;
}
